import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchActiveCategories } from '../../services/landing/categoryService';
import { ApiCategory } from '../../services/landing/categoryService';
import { Pagination } from '../../components/paginationlanding/Pagination';
import { usePagination } from '../../hooks/usePagination';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary: '#61257b',
    primaryDark: '#451759',
    secondary: '#c1348b',
    secondaryLight: '#ffd97c',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                setInView(true);
                return;
            }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

const isNew = (fecha: string) => new Date(fecha) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

// ── Loading ──
const LoadingSpinner = () => {
    const { isDark } = useTheme();
    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDark ? '#0b0614' : '#fff' }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    width: '48px', height: '48px', borderRadius: '50%',
                    border: `3px solid ${colors.primary}20`,
                    borderTop: `3px solid ${colors.primary}`,
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto 16px',
                }} />
                <div style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', fontFamily: "'DM Sans', sans-serif" }}>
                    Cargando servicios...
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

// ── Error ──
const ErrorState = ({ message }: { message: string }) => {
    const { isDark } = useTheme();
    return (
        <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', background: isDark ? '#0b0614' : '#fff' }}>
            <div style={{
                textAlign: 'center', padding: '40px',
                background: isDark ? '#150d24' : '#fff5f7', borderRadius: '20px',
                border: `1.5px solid ${colors.primary}20`,
            }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>⚠️</div>
                <div style={{ color: colors.primary, fontWeight: '600', fontFamily: "'DM Sans', sans-serif" }}>{message}</div>
            </div>
        </div>
    );
};

// ── Service Card con BorderGlow ──
const ServiceCard = ({ category, onClick, index }: {
    category: ApiCategory;
    onClick: (path: string) => void;
    index: number;
}) => {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [hovered, setHovered] = useState(false);
    const accentColors = [colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight, colors.primaryDark, colors.accent];
    const accent = accentColors[index % accentColors.length];

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(32px)',
                transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.07}s`,
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <BorderGlow
                borderRadius={24}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
                onClick={() => onClick(`/servicios/categoria/${category.slug}`)}
                style={{ cursor: 'pointer' }}
            >
                <div
                    className="service-card-inner"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                    }}
                >
                    {/* Image side */}
                    <div style={{
                        flex: '0 0 380px',
                        minHeight: '260px',
                        position: 'relative',
                        overflow: 'hidden',
                        background: `linear-gradient(145deg, ${accent}dd, ${accent}88)`,
                    }}>
                        <img
                            src={category.imagen || '/placeholder-service.jpg'}
                            alt={category.nombre}
                            style={{
                                position: 'absolute', inset: 0,
                                width: '100%', height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.5s cubic-bezier(.16,1,.3,1)',
                                transform: hovered ? 'scale(1.07)' : 'scale(1)',
                            }}
                            loading="lazy"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        {/* Overlay gradient adaptativo */}
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: isDark
                                ? `linear-gradient(to right, transparent 60%, #150d24 100%)`
                                : `linear-gradient(to right, transparent 60%, #fff 100%)`,
                            pointerEvents: 'none',
                        }} />

                        {/* "Nuevo" badge */}
                        {isNew(category.fecha_creacion) && (
                            <div style={{
                                position: 'absolute', top: '16px', left: '16px',
                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                                color: '#fff', borderRadius: '100px',
                                padding: '4px 14px', fontSize: '0.72rem', fontWeight: '700',
                                letterSpacing: '0.06em', textTransform: 'uppercase',
                                boxShadow: `0 4px 12px ${colors.primary}40`,
                                fontFamily: "'DM Sans', sans-serif",
                            }}>
                                Nuevo
                            </div>
                        )}

                        {/* Accent bar bottom */}
                        <div style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px',
                            background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
                            opacity: hovered ? 1 : 0,
                            transition: 'opacity 0.3s',
                        }} />
                    </div>

                    {/* Content side */}
                    <div style={{
                        flex: 1, minWidth: '280px',
                        padding: '36px 40px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    }}>
                        {/* Category tag */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                            <span style={{
                                fontSize: '0.7rem', fontWeight: '700',
                                letterSpacing: '0.14em', textTransform: 'uppercase',
                                color: accent,
                            }}>
                                Servicio
                            </span>
                            <div style={{ height: '2px', width: '28px', borderRadius: '2px', background: accent }} />
                        </div>

                        {/* Title */}
                        <h3 style={{
                            fontSize: 'clamp(1.3rem, 2.2vw, 1.75rem)',
                            fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a',
                            lineHeight: '1.2', letterSpacing: '-0.02em',
                            marginBottom: '14px',
                            fontFamily: "'DM Sans', sans-serif",
                        }}>
                            <Link to={`/servicios/categoria/${category.slug}`} onClick={event => event.stopPropagation()} style={{color: 'inherit', textDecoration: 'none'}}>{category.nombre}</Link>
                        </h3>

                        {/* Description */}
                        <p style={{
                            fontSize: '0.97rem', color: isDark ? '#cbd5e1' : '#64748b',
                            lineHeight: '1.8', marginBottom: '28px',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical' as const,
                            overflow: 'hidden',
                        }}>
                            {category.descripcion || 'Espacios y servicios profesionales adaptados a las necesidades de tu equipo.'}
                        </p>

                        {/* CTA button */}
                        <div>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                background: isDark ? 'linear-gradient(135deg, #61257B, #C1348B)' : accent,
                                color: '#fff',
                                borderRadius: '12px', padding: '10px 22px',
                                fontSize: '0.85rem', fontWeight: '700',
                                letterSpacing: '0.02em',
                                boxShadow: `0 4px 16px ${accent}40`,
                                transition: 'all 0.25s cubic-bezier(.16,1,.3,1)',
                                transform: hovered ? 'translateX(4px)' : 'translateX(0)',
                                fontFamily: "'DM Sans', sans-serif",
                            }}>
                                Ver detalles →
                            </span>
                        </div>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
};

// ── Main Services Component ──
const Services: React.FC = () => {
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [categories, setCategories] = useState<ApiCategory[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [heroRef, heroInView] = useInView(0.1);
    const [heroForced, setHeroForced] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setHeroForced(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const {
        currentPage,
        totalPages,
        currentItems,
        handlePageChange,
    } = usePagination(categories, 4);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                setLoading(true);
                const data = await fetchActiveCategories();
                setCategories(data);
                setError(null);
            } catch (err: any) {
                setError('No se pudieron cargar los servicios. Por favor, intenta de nuevo más tarde.');
            } finally {
                setLoading(false);
            }
        };
        loadCategories();
    }, []);

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorState message={error} />;

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: isDark ? '#0b0614' : '#fff', minHeight: '100vh', transition: 'background-color 0.3s ease' }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes blobFloat {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        .blob-anim { animation: blobFloat 9s ease-in-out infinite; }

        .fade-up-svc {
          opacity: 0; transform: translateY(28px);
          transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1);
        }
        .fade-up-svc.in { opacity: 1; transform: translateY(0); }
        .fade-up-svc.d1 { transition-delay: 0.1s; }
        .fade-up-svc.d2 { transition-delay: 0.2s; }
        .fade-up-svc.d3 { transition-delay: 0.3s; }

        @media (max-width: 768px) {
          .service-card-inner { flex-direction: column !important; }
        }
      `}</style>

            {/* ═══ HERO ═══ */}
            <section style={{
                background: isDark
                    ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #faf6fb 55%, #f6ecf5 100%)',
                padding: 'clamp(90px, 12vw, 130px) 32px clamp(50px, 7vw, 70px)',
                position: 'relative', overflow: 'hidden',
            }}>
                {/* Blob animado */}
                <div className="blob-anim" style={{
                    position: 'absolute', top: '-80px', right: '-80px',
                    width: '420px', height: '420px',
                    background: isDark
                        ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))'
                        : 'linear-gradient(135deg, rgba(97,37,123,0.12), rgba(193,52,139,0.08))',
                    pointerEvents: 'none',
                }} />
                {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
                <HexagonCornerDecoration
                    variant="horizontal-degrade"
                    direction="left-to-right"
                    opacity={isDark ? 0.70 : 0.50}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                <div ref={heroRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                    <h1 className={`fade-up-svc d1 ${(heroInView || heroForced) ? 'in' : ''}`} style={{
                        fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                        fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                        lineHeight: '1.08', letterSpacing: '-0.025em',
                        marginBottom: '20px',
                    }}>
                        Nuestros <span style={{ background: 'linear-gradient(135deg, #61257b 0%, #c1348b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Servicios</span>
                    </h1>

                    <p className={`fade-up-svc d2 ${(heroInView || heroForced) ? 'in' : ''}`} style={{
                        fontSize: '1.05rem', color: isDark ? '#cbd5e1' : '#64748b',
                        maxWidth: '480px', margin: '0 auto',
                        lineHeight: '1.7', fontWeight: '400',
                    }}>
                        Soluciones integrales para emprendedores y empresas que buscan crecer en Bolivia.
                    </p>

                    <div className={`fade-up-svc d3 ${(heroInView || heroForced) ? 'in' : ''}`} style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
                        <BrandStripes variant="scroll-prompt" size="md" promptText="Descubre nuestros servicios" />
                    </div>
                </div>
            </section>

            {/* ═══ CARDS ═══ */}
            <section style={{ padding: '60px 32px 100px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                    {currentItems.map((category, index) => (
                        <ServiceCard
                            key={category.id}
                            category={category}
                            onClick={navigate}
                            index={index}
                        />
                    ))}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                    <div style={{ marginTop: '48px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}

                {/* Barra decorativa final */}
                <div style={{ marginTop: '64px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes size="md" />
                </div>
            </section>
        </div>
    );
};

export default Services;